export type Panel = "computer" | "routines" | "settings" | "routine" | "group-settings" | null;
export type RightPanelState = { panel: Panel; routineId?: string };

/** A group only has its company panel; a bot cannot keep the group tab or an unloaded routine. */
export function panelForChat(
  panel: Panel,
  context: { group: boolean; bot: boolean; routineReady: boolean },
): Panel {
  if (context.group) return "group-settings";
  if (panel === "group-settings" || (panel === "routine" && !context.routineReady)) {
    return "settings";
  }
  return panel ?? (context.bot ? "settings" : null);
}

/** Panels that are safe to remember across reloads. */
const durablePanels: readonly Panel[] = [
  "computer",
  "routines",
  "settings",
  "routine",
  "group-settings",
  null,
];

export function rightPanelStorageKey(
  userId: string,
  spaceId: string,
  kind: "bot" | "group",
  targetId: string,
): string {
  return `clvabot:right-panel-state:${userId}:${spaceId}:${kind}:${targetId}`;
}

export function readRightPanelState(key: string): RightPanelState {
  try {
    const value: unknown = JSON.parse(localStorage.getItem(key) ?? "null");
    if (
      !value ||
      typeof value !== "object" ||
      !("panel" in value) ||
      !durablePanels.includes(value.panel as Panel)
    )
      return { panel: null };
    return {
      panel: value.panel as Panel,
      ...(value.panel === "routine" &&
      "routineId" in value &&
      typeof value.routineId === "string" &&
      value.routineId
        ? { routineId: value.routineId }
        : {}),
    };
  } catch {
    return { panel: null };
  }
}

export function writeRightPanelState(key: string, panel: Panel, routineId?: string) {
  try {
    localStorage.setItem(
      key,
      JSON.stringify({ panel, ...(panel === "routine" && routineId ? { routineId } : {}) }),
    );
  } catch {
    // Restricted storage must not prevent opening or closing the panel.
  }
}
